import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { ArrowLeftRight, Loader2, Plug, ExternalLink, CheckCircle2, Settings2 } from "lucide-react";
import { toast } from "sonner";

interface Status {
  connected: boolean;
  grafton_count: number;
  hubspot_count: number;
  redirect_enabled: boolean;
  account: { portalId?: number; uiDomain?: string; dataHostingLocation?: string };
}

export function HubSpotPanel({ onChange }: { onChange?: () => void }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const [status, setStatus] = useState<Status | null>(null);
  const [openConnect, setOpenConnect] = useState(false);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    try {
      const r = await fetch(`${BACKEND}/api/hubspot/status`, { credentials: "include" });
      if (r.ok) setStatus(await r.json());
    } catch {
      /* noop */
    }
  };

  useEffect(() => {
    load();
  }, []);

  const connect = async () => {
    if (!token.trim().startsWith("pat-")) {
      toast.error("Le token doit commencer par 'pat-' (Private App Token)");
      return;
    }
    setBusy("connect");
    try {
      const r = await fetch(`${BACKEND}/api/hubspot/connect`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ api_token: token.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Connexion échouée");
      toast.success(`HubSpot connecté (portal ${data.account?.portalId})`);
      setOpenConnect(false);
      setToken("");
      load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const importContacts = async () => {
    if (!confirm("Importer tous les contacts HubSpot vers Grafton ? (les doublons sont fusionnés par email)")) return;
    setBusy("import");
    try {
      const r = await fetch(`${BACKEND}/api/hubspot/import`, {
        method: "POST",
        credentials: "include",
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Import échoué");
      toast.success(
        `Import terminé : ${data.created} créés · ${data.updated} mis à jour · ${data.skipped_no_email} ignorés`,
      );
      load();
      onChange?.();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const exportContacts = async () => {
    if (!confirm("Exporter tous les contacts Grafton vers HubSpot ?")) return;
    setBusy("export");
    try {
      const r = await fetch(`${BACKEND}/api/hubspot/export`, {
        method: "POST",
        credentials: "include",
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Export échoué");
      toast.success(
        `Export terminé : ${data.created} créés · ${data.updated} mis à jour · ${data.failed} échecs`,
      );
      load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async () => {
    if (!confirm("Déconnecter HubSpot ?")) return;
    try {
      await fetch(`${BACKEND}/api/hubspot/disconnect`, {
        method: "DELETE",
        credentials: "include",
      });
      toast.success("HubSpot déconnecté");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  const toggleRedirect = async (enabled: boolean) => {
    try {
      await fetch(`${BACKEND}/api/hubspot/redirect-mode`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
      });
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  if (!status) return null;

  return (
    <>
      <Card className="border-orange-500/30 bg-orange-500/5" data-testid="hubspot-panel">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="h-9 w-9 rounded-md bg-orange-500/15 flex items-center justify-center flex-shrink-0">
                <span className="text-lg">🟠</span>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">HubSpot CRM</span>
                  {status.connected ? (
                    <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40 text-[10px]">
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Connecté · portal {status.account.portalId}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px]">
                      Non connecté
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {status.connected
                    ? `Synchronisation bidirectionnelle Grafton ↔ HubSpot · ${status.grafton_count} contacts Grafton`
                    : "Connectez votre Private App Token HubSpot pour synchroniser vos contacts."}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {status.connected ? (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={importContacts}
                    disabled={busy === "import"}
                    data-testid="hubspot-import-btn"
                    className="gap-1.5"
                  >
                    {busy === "import" ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <ArrowLeftRight className="h-3.5 w-3.5" />
                    )}
                    HubSpot → Grafton
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={exportContacts}
                    disabled={busy === "export"}
                    data-testid="hubspot-export-btn"
                    className="gap-1.5"
                  >
                    {busy === "export" ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <ArrowLeftRight className="h-3.5 w-3.5 rotate-180" />
                    )}
                    Grafton → HubSpot
                  </Button>
                  {status.account.uiDomain && (
                    <Button size="sm" variant="ghost" asChild data-testid="hubspot-open">
                      <a
                        href={`https://${status.account.uiDomain}/contacts/${status.account.portalId}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ouvrir HubSpot
                      </a>
                    </Button>
                  )}
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-muted/30">
                    <Switch
                      checked={status.redirect_enabled}
                      onCheckedChange={toggleRedirect}
                      data-testid="hubspot-redirect-toggle"
                    />
                    <span className="text-[10px] text-muted-foreground">Mode HubSpot</span>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={disconnect}
                    data-testid="hubspot-disconnect"
                    className="text-destructive"
                  >
                    <Settings2 className="h-3.5 w-3.5" />
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  onClick={() => setOpenConnect(true)}
                  data-testid="hubspot-connect-btn"
                  className="gap-1.5"
                >
                  <Plug className="h-3.5 w-3.5" /> Connecter HubSpot
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={openConnect} onOpenChange={setOpenConnect}>
        <DialogContent className="max-w-md" data-testid="hubspot-connect-dialog">
          <DialogHeader>
            <DialogTitle>Connecter HubSpot</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Collez votre Private App Token (format <code className="text-xs">pat-eu1-xxxx...</code>).
            </p>
            <Input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="pat-eu1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              data-testid="hubspot-token-input"
            />
            <p className="text-xs text-muted-foreground">
              Pour le générer : HubSpot → Settings → Integrations → Private Apps → Create Private App →
              scopes <code>crm.objects.contacts.read</code> + <code>crm.objects.contacts.write</code>.
            </p>
            <Button onClick={connect} disabled={busy === "connect"} className="w-full" data-testid="hubspot-connect-submit">
              {busy === "connect" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Tester et connecter
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
